import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { BILLING_MONTHS, type BillingSearch } from '../billing-search';

interface Props {
  value: BillingSearch;
  onChange: (next: BillingSearch) => void;
  /** Injected so the "step from no selection" anchor is deterministic in tests.
   * UTC, like the period arithmetic in billing-search. */
  today?: Date;
}

/**
 * Prev / next month stepper (LR-557, mock's toolbar control). It is a faster
 * hand on the SAME state the Year/Month selects edit — one URL, no second
 * source of truth. With nothing selected it anchors on the current month, so
 * "prev" from the unfiltered list means "show me last month"; a year-only
 * filter is the range Jan–Dec of that year, and a step exits the range rather
 * than discarding the picked year.
 */
export const BillingMonthNav: React.FC<Props> = ({
  value,
  onChange,
  today = new Date(),
}) => {
  const step = (delta: 1 | -1) => {
    let year: number;
    let month: number;
    if (value.year !== undefined && value.month !== undefined) {
      year = value.year;
      month = value.month + delta;
    } else if (value.year !== undefined) {
      // A year-only filter is the range Jan–Dec of that year: step to the
      // month just outside it, never to a month around today's date — that
      // would silently discard the year the user picked.
      year = value.year;
      month = delta === 1 ? 13 : 0;
    } else {
      // Nothing selected: anchor on the current month (UTC, like the period
      // arithmetic in billing-search), so "prev" means "show me last month".
      year = today.getUTCFullYear();
      month = today.getUTCMonth() + 1 + delta;
    }
    if (month === 0) {
      month = 12;
      year -= 1;
    } else if (month === 13) {
      month = 1;
      year += 1;
    }
    onChange({ ...value, year, month });
  };

  const label =
    value.year !== undefined && value.month !== undefined
      ? `${BILLING_MONTHS.find((m) => m.value === value.month)?.label} ${value.year}`
      : value.year !== undefined
        ? String(value.year)
        : 'All months';

  return (
    // Joined button group, per the mock: chevron | label | chevron in one
    // hairline box.
    <nav
      aria-label="Statement month"
      className="inline-flex items-stretch overflow-hidden rounded-md border border-gray-300 bg-white"
    >
      <button
        type="button"
        aria-label="Previous month"
        onClick={() => step(-1)}
        className="px-2 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      {/* The widest label sits invisibly in the same grid cell, so the box and
          the chevrons stay put as the month changes - measured in whatever font
          the browser renders, not a guessed pixel width. */}
      <span className="grid border-x border-gray-200 px-3.5 py-1 text-center text-sm font-semibold tabular-nums text-gray-900">
        <span aria-hidden="true" className="invisible col-start-1 row-start-1">
          September 0000
        </span>
        <span aria-live="polite" className="col-start-1 row-start-1">
          {label}
        </span>
      </span>
      <button
        type="button"
        aria-label="Next month"
        onClick={() => step(1)}
        className="px-2 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
      >
        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
};
